import { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import { LazyMap } from '../../../../components/Map/Lazy';
import { PathStrategy } from '../../../../components/Map/enums/path-strategy.enum';
import { StateStrategy } from '../../../../components/Map/enums/state-strategy.enum';
import { useFormikOnNextTick } from '../../../../hooks/useFormikOnNextTick.hook';
import { useUiContext } from '../../../../contexts/UiContext/hooks/useUiContext.hook';
import { BackgroundOperationType } from '../../../../contexts/UiContext/enums/background-operation-type.enum';
import type { Feature } from '../../../../components/Map/types/map/feature.type';
import type { ReferencesContext_AddressableConfig } from '../../../../contexts/ReferencesContext';

import { useFromAddressToLatLon } from '../../hooks/useFromAddressToLatLon.hook';
import { useFromLatLonToAddressForm } from '../../hooks/useFromLatLonToAddressForm.hook';
import { checkIfIsAddressEmpty } from '../../utils/check-if-is-address-empty.util';
import type { AddressForm } from '../../types/address-form.type';

interface Props {
  readonly config: ReferencesContext_AddressableConfig;
  readonly id: string;
}

export const AddressInterativeMapFormStep = ({
  config,
  id,
}: Readonly<Props>) => {
  const { values } = useFormikContext<AddressForm>();

  const { setValues: setValuesOnNextTick } = useFormikOnNextTick<AddressForm>();

  const [selectedFeature, setSelectedFeature] = useState<Feature | undefined>();

  const hasInitialPosition = !checkIfIsAddressEmpty(values);

  const { data: initialPosition } = useFromAddressToLatLon({
    address: values,
    enabled: hasInitialPosition,
  });

  const newSelectedPosition = useMemo(
    () =>
      selectedFeature
        ? 'x' in selectedFeature.position
          ? {
              lat: selectedFeature.position.y,
              lon: selectedFeature.position.x,
            }
          : selectedFeature.position
        : undefined,
    [selectedFeature],
  );

  const { data: newAddress, isLoading: isResolvingSelectedPosition } =
    useFromLatLonToAddressForm({
      latLon: newSelectedPosition,
    });

  const { startBackgroundOperation, endBackgroundOperation } = useUiContext();

  // The address of a selected point comes from several services: the wizard
  // waits for it, showing the progress, before leaving the map.
  useEffect(() => {
    if (!newSelectedPosition || !isResolvingSelectedPosition) {
      return undefined;
    }

    const backgroundOperationKey = {
      type: BackgroundOperationType.AddressPointResolution,
      identifier: id,
    };

    startBackgroundOperation(backgroundOperationKey);

    return () => {
      endBackgroundOperation(backgroundOperationKey);
    };
  }, [
    endBackgroundOperation,
    id,
    isResolvingSelectedPosition,
    newSelectedPosition,
    startBackgroundOperation,
  ]);

  useEffect(() => {
    if (
      !newAddress ||
      !selectedFeature ||
      isEqual(selectedFeature, { position: initialPosition })
    ) {
      return;
    }

    setValuesOnNextTick((oldAddress) => ({
      ...newAddress,
      _id: oldAddress._id,
      typeIds: oldAddress.typeIds,
    }));
  }, [selectedFeature, newAddress, setValuesOnNextTick, initialPosition]);

  return (
    <Grid
      container
      spacing={2}
      sx={{ alignContent: 'flex-start', width: '100%', height: '100%' }}
    >
      <Grid
        size={12}
        sx={{
          pt: 1,
          pb: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
        }}
      >
        {hasInitialPosition && !initialPosition ? (
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'row',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <LazyMap
            id={`${id}-map`}
            initialPosition={initialPosition}
            pathStrategy={PathStrategy.Stateful}
            selectedFeature={selectedFeature}
            setSelectedFeature={setSelectedFeature}
            stateStrategy={StateStrategy.Controlled}
            useCase={{
              id: config.code,
              allowElementsList: false,
              description: config.label,
            }}
          />
        )}
      </Grid>
    </Grid>
  );
};
